(ns manatee.admin.rejection-option.components
  "components for the admin/rejections tab."
  (:require [manatee.admin.rejection-option.handlers :as rejection-handlers]
            [manatee.admin.rejection-option.subs :as rejection-subs]
            [manatee.spec.core :as spec]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [rothko.button.component :as button-component]
            [rothko.collection-components.core :as collection]
            [rothko.collection-components.table.component :as table-component]
            [rothko.form2.checkbox.component :as checkbox]
            [rothko.form2.component :as form]
            [rothko.form2.input.component :as input]
            [rothko.form2.select.core :as select]
            [rothko.form2.select.single-select.component :as single-select]
            [rothko.form2.validation :as validation]
            [rothko.panel.component :as panel-component]))

(defn- select-rejection-panel-view
  "Switch to the list view if LIST-VIEW? is true, the form view (to view the
   configuration identified by ID, if not nil) otherwise."
  [rejection-option-id list-view?]
  (rf/dispatch [::rejection-handlers/set-rejection-option rejection-option-id])
  (rf/dispatch [::rejection-handlers/set-list-view list-view?]))

(defn- rejection-fields
  [rejection]
  (let [{:keys [type medium value label archived?]} rejection]
    {:type {:appearance #{:primary}
            ::form/name :type
            ::form/label "Type"
            ::form/placeholder "Choose type..."
            ::form/initial-value type
            ::form/disabled? false
            ::validation/spec ::spec/some?
            ::validation/messages {::spec/some* "Must not be empty"}
            ::select/options [{:value :answer, :representation "Answer"} {:value :part, :representation "Part"}]}
     :medium {:appearance #{:primary}
              ::form/name :medium
              ::form/label "Medium"
              ::form/placeholder "Choose medium..."
              ::form/initial-value medium
              ::form/disabled? false
              ::validation/spec ::spec/some?
              ::validation/messages {::spec/some* "Must not be empty"}
              ::select/options [{:value :speech, :representation "Speech"} {:value :text, :representation "Text"}]}
     :value {::form/name :value
             ::form/label "Value"
             ::form/initial-value (when value (name value))
             ::form/disabled? false
             ::input/type :text
             ::validation/spec ::spec/not-empty
             ::validation/messages {::spec/not-empty "Must not be empty"}
             :class [:admin-page__input]
             :appearance #{:email-label :primary}}
     :label {::form/name :label
             ::form/label "Label"
             ::form/initial-value label
             ::form/disabled? false
             ::input/type :text
             ::validation/spec ::spec/not-empty
             ::validation/messages {::spec/not-empty "Must not be empty"}
             :class [:admin-page__input]
             :appearance #{:email-label :primary}}
     :archived? {::form/name :archived?
                 ::form/initial-value archived?
                 ::checkbox/option true
                 ::checkbox/label "Archived?"}}))

(def back-config
  {:appearance #{:tertiary}
   :type       :button
   :on-click   #(select-rejection-panel-view nil true)})

(defn upsert-config
  [submit!]
  {:appearance #{:primary}
   :type       :button
   :class      [:admin-page__submit-button]
   ;:disabled?  (not valid?)
   :on-click   submit!})

(defn- rejections-form-view
  "add/edit rejection-option form"
  [rejection-option]
  (let [title (if rejection-option "Edit rejection option" "New rejection option")
        rejection-fields (rejection-fields rejection-option)]
    [:div {:appearance #{:column}
           :class      [:admin-page__form-layout]}
     [:h4 {:class [:admin-page__header]} title]
     [:div {:class [:admin-page__forms]}
      [form/form
       {::form/name      :queue-form
        ::form/on-submit #(rf/dispatch (if rejection-option [::rejection-handlers/update-rejection % (:id rejection-option)] [::rejection-handlers/add-rejection %]))}
       {:type (:type rejection-fields)
        :medium (:medium rejection-fields)
        :value (:value rejection-fields)
        :label (:label rejection-fields)
        :archived? (:archived? rejection-fields)}
       (fn [{:keys [::form/submit!]
             :as   _form-props}
            {:keys [type medium value label archived?]
             :as   _field-props}]
         [:div
          [:form {:class [:admin-page__queue-form]}
           [:div.form-row.button-group
            [button-component/button back-config
             "Back"]]
           [single-select/field type]
           [single-select/field medium]
           [input/field value]
           [input/field label]
           [checkbox/field archived?]
           [:div.form-row.button-group
            [button-component/button (upsert-config submit!)
             (if rejection-option "Update" "Add")]]]])]]]))

(defn format-rejection-cells
  [{:keys [archived?] :as rejection}]
  (update rejection :archived? #(when archived? "✓")))

(defn- rejections-list-view
  "list of persisted restriction-options"
  [props]
  (let [show-archived? (r/atom false)]
    (fn []
      (let [{:keys [all-rejections active-rejections]} props
            rejections (->> (if @show-archived? all-rejections active-rejections)
                            (sort (fn [r1 r2] (compare
                                               (str (:type r1) (:medium r1) (:value r1))
                                               (str (:type r2) (:medium r2) (:value r2))))))]
        [:div {:appearance #{:column}
               :class      [:admin-page__table-layout]}
         [:h4 {:class [:admin-page__header]} "Rejection options"]
         [:div.form-row.button-group
          [button-component/button {:appearance #{:tertiary}
                                    :type       :button
                                    :on-click   #(select-rejection-panel-view nil false)}
           "New"]]
         [:div.checkbox__container
          {:class [:manatee]}
          [:input
           {:type           :checkbox
            :name           :show-archived
            :on-change      #(swap! show-archived? not)
            :disabled       false
            :defaultChecked false}]
          [:label {:for :show-archived} "Show archived configurations?"]]
         [table-component/component
          {:appearance #{:primary}
           ::collection/items (mapv format-rejection-cells rejections)
           ::collection/fields [{::collection/id :type ::collection/label "Type"}
                                {::collection/id :medium ::collection/label "Medium"}
                                {::collection/id :value ::collection/label "Value"}
                                {::collection/id :label ::collection/label "Label"}
                                {::collection/id :archived? ::collection/label "Archived?"}]
           ::table-component/row-on-click #(select-rejection-panel-view (:id %) false)}]]))))

(defn rejections-panel
  "The admin/rejections tab panel comprises one of the following views:
    - A list of existing rejection options.
    - A form to either enter a new rejection-option or to edit/delete an
      existing rejection-option"
  [props]
  (let [selected-rejection-option @(rf/subscribe [::rejection-subs/selected-rejection-option])
        list-view? @(rf/subscribe [::rejection-subs/list-view?])]
    [:<>
     [panel-component/panel {:appearance #{:primary}
                             :class      [:admin-page__panel]}
      (if list-view?
        [rejections-list-view props]
        [rejections-form-view selected-rejection-option])]]))
